import { clamp } from "../../core/math";
import { scheduleAttack, scheduleRelease, smoothSet, type Adsr } from "../envelope";
import { HARD_MAX_VOICES, type Instrument } from "./types";

export interface SamplerParams {
  rootNote: number; // MIDI note at which the sample plays at original pitch
  volume: number;
  /** One-shot: plays to the end regardless of note-off (pads). Gate: stops on note-off (keyboard). */
  mode: "oneshot" | "gate";
  attackSec: number;
  releaseSec: number;
}

export const DEFAULT_SAMPLER_PARAMS: SamplerParams = { rootNote: 60, volume: 0.8, mode: "gate", attackSec: 0.003, releaseSec: 0.15 };

interface SVoice { note: number; src: AudioBufferSourceNode; amp: GainNode; startSec: number; released: boolean; }

/** Pitch bend range accepted by the sampler (semitones). */
const MAX_BEND = 24;

/**
 * Single-sample instrument. Pitch is changed by playbackRate = 2^(Δ/12),
 * which also changes duration (documented limitation, AGENT.md §12).
 */
export class SamplerInstrument implements Instrument<SamplerParams> {
  readonly kind = "sampler";
  readonly output: GainNode;
  private _params: SamplerParams;
  private buffer: AudioBuffer | null = null;
  private voices: SVoice[] = [];
  private bendSemitones = 0;

  constructor(private ctx: BaseAudioContext, params: Partial<SamplerParams> = {}) {
    this._params = { ...DEFAULT_SAMPLER_PARAMS, ...params };
    this.output = ctx.createGain();
    this.output.gain.value = this._params.volume;
  }

  get params(): SamplerParams { return this._params; }
  get activeVoices(): number { return this.voices.length; }
  get hasSample(): boolean { return this.buffer !== null; }

  setBuffer(buffer: AudioBuffer | null): void {
    this.hardStop();
    this.buffer = buffer;
  }

  static playbackRateFor(note: number, rootNote: number): number {
    return Math.pow(2, (note - rootNote) / 12);
  }

  noteOn(note: number, velocity: number, timeSec = this.ctx.currentTime): void {
    if (!this.buffer) return;
    while (this.voices.length >= HARD_MAX_VOICES) this.stopVoice(this.voices[0]!, timeSec);
    const src = this.ctx.createBufferSource();
    src.buffer = this.buffer;
    src.playbackRate.value = SamplerInstrument.playbackRateFor(note + this.bendSemitones, this._params.rootNote);
    const amp = this.ctx.createGain();
    amp.gain.value = 0;
    src.connect(amp).connect(this.output);
    scheduleAttack(amp.gain, this.env(), clamp(velocity, 0, 1), timeSec);
    src.start(timeSec);
    const v: SVoice = { note, src, amp, startSec: timeSec, released: false };
    src.onended = () => this.cleanup(v);
    this.voices.push(v);
  }

  noteOff(note: number, timeSec = this.ctx.currentTime): void {
    if (this._params.mode === "oneshot") return;
    for (const v of this.voices) if (v.note === note && !v.released) this.release(v, timeSec);
  }

  releaseAll(timeSec = this.ctx.currentTime): void {
    for (const v of this.voices) if (!v.released) this.release(v, timeSec);
  }

  hardStop(): void {
    const now = this.ctx.currentTime;
    for (const v of [...this.voices]) this.stopVoice(v, now);
  }

  /** Bend all sounding and future notes; like transposition, it also changes playback speed. */
  pitchBend(semitones: number): void {
    this.bendSemitones = clamp(semitones, -MAX_BEND, MAX_BEND);
    for (const v of this.voices) {
      smoothSet(v.src.playbackRate, SamplerInstrument.playbackRateFor(v.note + this.bendSemitones, this._params.rootNote), this.ctx, 0.005);
    }
  }

  setParams(params: Partial<SamplerParams>): void {
    this._params = { ...this._params, ...params };
    if (params.volume !== undefined) smoothSet(this.output.gain, this._params.volume, this.ctx);
  }

  dispose(): void {
    this.hardStop();
    this.output.disconnect();
    this.buffer = null;
  }

  private env(): Adsr {
    return { attackSec: this._params.attackSec, decaySec: 0.001, sustain: 1, releaseSec: this._params.releaseSec };
  }

  private release(v: SVoice, timeSec: number): void {
    v.released = true;
    const end = scheduleRelease(v.amp.gain, this.env(), 1, v.startSec, Math.max(timeSec, v.startSec));
    try { v.src.stop(end); } catch { /* already stopped */ }
  }

  private stopVoice(v: SVoice, timeSec: number): void {
    v.amp.gain.cancelScheduledValues(timeSec);
    v.amp.gain.setTargetAtTime(0, timeSec, 0.004);
    try { v.src.stop(timeSec + 0.02); } catch { /* already stopped */ }
    this.voices = this.voices.filter((x) => x !== v);
  }

  private cleanup(v: SVoice): void {
    this.voices = this.voices.filter((x) => x !== v);
    v.src.disconnect();
    v.amp.disconnect();
  }
}
